// color design tokens export
export const tokensDark = {
    grey: {
        0: "#ffffff",
        10: "#f7f7f9",
        50: "#f0f1f3",
        100: "#e2e3e7",
        200: "#c6c8cf",
        300: "#a7aab4",
        400: "#888b97",
        500: "#696d7a",
        600: "#52555f",
        700: "#3c3e47",
        800: "#26272d",
        900: "#141519",
        1000: "#000000",
    },
    primary: {
        // navy
        100: "#dde1f5",
        200: "#b7bee8",
        300: "#8d96d3",
        400: "#4b5590",
        500: "#262f63",
        600: "#181d45",
        700: "#12162f",
        800: "#0b0e1f",
        900: "#05060d",
    },
    secondary: {
        // gold
        50: "#fcf2de",
        100: "#fbe7c4",
        200: "#f1cd82",
        300: "#e8b54a",
        400: "#dfa530",
        500: "#c68f22",
        600: "#a6741b",
        700: "#7c5714",
        800: "#543a0d",
        900: "#2e1f07",
    },
};

// function that reverses the color palette
function reverseTokens(tokensDark) {
    const reversedTokens = {};
    Object.entries(tokensDark).forEach(([key, val]) => {
        const keys = Object.keys(val);
        const values = Object.values(val);
        const length = keys.length;
        const reversedObj = {};
        for (let i = 0; i < length; i++) {
            reversedObj[keys[i]] = values[length - i - 1];
        }
        reversedTokens[key] = reversedObj;
    });
    return reversedTokens;
}
export const tokensLight = reverseTokens(tokensDark);

// mui theme settings
export const themeSettings = (mode) => {
    return {
        palette: {
            mode: mode,
            ...(mode === "dark"
                ? {
                    // palette values for dark mode
                    primary: {
                        ...tokensDark.primary,
                        main: tokensDark.primary[400],
                        light: tokensDark.primary[400],
                    },
                    secondary: {
                        ...tokensDark.secondary,
                        main: tokensDark.secondary[300],
                    },
                    neutral: {
                        ...tokensDark.grey,
                        main: tokensDark.grey[500],
                    },
                    background: {
                        default: tokensDark.primary[600],
                        alt: tokensDark.primary[500],
                        paper: tokensDark.primary[500],
                    },
                    divider: "rgba(221, 225, 245, 0.12)",
                }
                : {
                    // palette values for light mode
                    primary: {
                        ...tokensLight.primary,
                        main: tokensDark.grey[50],
                        light: tokensDark.grey[100],
                    },
                    secondary: {
                        ...tokensLight.secondary,
                        main: tokensDark.secondary[600],
                        light: tokensDark.secondary[700],
                    },
                    neutral: {
                        ...tokensLight.grey,
                        main: tokensDark.grey[500],
                    },
                    background: {
                        default: tokensDark.grey[0],
                        alt: "#f6f7fb",
                        paper: "#ffffff",
                    },
                    divider: "rgba(24, 29, 69, 0.09)",
                }),
        },
        shape: {
            borderRadius: 12,
        },
        typography: {
            fontFamily: ["Inter", "sans-serif"].join(","),
            fontSize: 14,
            h1: {
                fontFamily: ["Inter", "sans-serif"].join(","),
                fontSize: 40,
                fontWeight: 700,
                letterSpacing: "-0.02em",
            },
            h2: {
                fontFamily: ["Inter", "sans-serif"].join(","),
                fontSize: 32,
                fontWeight: 700,
                letterSpacing: "-0.02em",
            },
            h3: {
                fontFamily: ["Inter", "sans-serif"].join(","),
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: "-0.01em",
            },
            h4: {
                fontFamily: ["Inter", "sans-serif"].join(","),
                fontSize: 20,
                fontWeight: 600,
            },
            h5: {
                fontFamily: ["Inter", "sans-serif"].join(","),
                fontSize: 16,
                fontWeight: 600,
            },
            h6: {
                fontFamily: ["Inter", "sans-serif"].join(","),
                fontSize: 14,
                fontWeight: 600,
            },
            button: {
                textTransform: "none",
                fontWeight: 600,
            },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    "*": {
                        scrollbarWidth: "thin",
                    },
                    "::-webkit-scrollbar": {
                        width: "10px",
                        height: "10px",
                    },
                    "::-webkit-scrollbar-track": {
                        background: "transparent",
                    },
                    "::-webkit-scrollbar-thumb": {
                        backgroundColor:
                            mode === "dark"
                                ? "rgba(221, 225, 245, 0.18)"
                                : "rgba(24, 29, 69, 0.18)",
                        borderRadius: "8px",
                    },
                    "::-webkit-scrollbar-thumb:hover": {
                        backgroundColor:
                            mode === "dark"
                                ? "rgba(221, 225, 245, 0.32)"
                                : "rgba(24, 29, 69, 0.32)",
                    },
                },
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true,
                },
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        paddingLeft: "1.1rem",
                        paddingRight: "1.1rem",
                        paddingTop: "0.55rem",
                        paddingBottom: "0.55rem",
                        transition: "transform 120ms ease, box-shadow 120ms ease",
                    },
                    contained: {
                        boxShadow: "0 4px 14px rgba(10, 13, 40, 0.18)",
                        "&:hover": {
                            boxShadow: "0 6px 18px rgba(10, 13, 40, 0.24)",
                            transform: "translateY(-1px)",
                        },
                    },
                    outlined: {
                        borderWidth: "1.5px",
                        "&:hover": {
                            borderWidth: "1.5px",
                        },
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        transition: "background-color 120ms ease, transform 120ms ease",
                    },
                },
            },
            MuiPaper: {
                defaultProps: {
                    elevation: 0,
                },
                styleOverrides: {
                    root: {
                        backgroundImage: "none",
                    },
                    elevation1: {
                        boxShadow:
                            mode === "dark"
                                ? "0 1px 2px rgba(0, 0, 0, 0.3)"
                                : "0 1px 2px rgba(24, 29, 69, 0.08)",
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 16,
                        border:
                            mode === "dark"
                                ? "1px solid rgba(221, 225, 245, 0.08)"
                                : "1px solid rgba(24, 29, 69, 0.07)",
                        boxShadow:
                            mode === "dark"
                                ? "0 8px 24px rgba(2, 3, 12, 0.35)"
                                : "0 8px 24px rgba(24, 29, 69, 0.08)",
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        border: "none",
                        boxShadow:
                            mode === "dark"
                                ? "1px 0 0 rgba(221, 225, 245, 0.08)"
                                : "1px 0 0 rgba(24, 29, 69, 0.07)",
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: 999,
                        fontWeight: 600,
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                    },
                },
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        borderColor:
                            mode === "dark"
                                ? "rgba(221, 225, 245, 0.12)"
                                : "rgba(24, 29, 69, 0.09)",
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        transition: "background-color 120ms ease, color 120ms ease",
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        borderRadius: 8,
                        fontSize: "0.75rem",
                    },
                },
            },
            MuiLink: {
                defaultProps: {
                    underline: "hover",
                },
            },
        },
    };
};
